import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import banner from "../images/banner.png";
import Store from "../components/Store";
import Footer from "../components/Footer";
import { QUERY_ALL_STORES, QUERY_ME } from "../utils/queries";
import { PiCompassBold } from "react-icons/pi";
import { FiSearch } from "react-icons/fi";

const Home = () => {
  const { loading, error, data } = useQuery(QUERY_ALL_STORES);
  const { data: meData } = useQuery(QUERY_ME);
  const [sortBy, setSortBy] = useState("favorites-first");
  const [selectedTags, setSelectedTags] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const stores = data?.allStores || [];
  const favoriteStoreIds = useMemo(() => {
    return new Set(meData?.me?.favoriteStores?.map((store) => store._id) || []);
  }, [meData]);

  // Extract unique tags from all stores
  const allTags = useMemo(() => {
    const tagSet = new Set();
    stores.forEach((store) => {
      if (store.tags && Array.isArray(store.tags)) {
        store.tags.forEach((tag) => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [stores]);

  // Filter by tags, search, and sort
  const filteredAndSortedStores = useMemo(() => {
    let filtered = stores;

    // Filter by search term
    if (searchTerm.trim() !== "") {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter((store) => {
        const nameMatch = (store.storeName || store.name || "")
          .toLowerCase()
          .includes(lower);
        const descMatch = (store.storeDescription || store.description || "")
          .toLowerCase()
          .includes(lower);
        const tagsMatch = (store.tags || []).some((tag) =>
          tag.toLowerCase().includes(lower),
        );
        return nameMatch || descMatch || tagsMatch;
      });
    }

    // Filter by selected tags - only show stores with ALL selected tags
    if (selectedTags.length > 0) {
      filtered = stores.filter((store) =>
        selectedTags.every((tag) => store.tags && store.tags.includes(tag)),
      );
    }

    // Apply sorting
    return [...filtered].sort((a, b) => {
      const aName = (a.storeName || a.name || "").toLowerCase();
      const bName = (b.storeName || b.name || "").toLowerCase();
      const aDate = new Date(a.dateAdded || 0).getTime();
      const bDate = new Date(b.dateAdded || 0).getTime();
      const aIsFavorite = favoriteStoreIds.has(a._id);
      const bIsFavorite = favoriteStoreIds.has(b._id);

      switch (sortBy) {
        case "favorites-first":
          if (aIsFavorite && !bIsFavorite) return -1;
          if (!aIsFavorite && bIsFavorite) return 1;
          return aName.localeCompare(bName);
        case "name-asc":
          return aName.localeCompare(bName);
        case "name-desc":
          return bName.localeCompare(aName);
        case "newest":
          return bDate - aDate;
        case "oldest":
          return aDate - bDate;
        default:
          return 0;
      }
    });
  }, [stores, sortBy, selectedTags, favoriteStoreIds, searchTerm]);

  if (loading) return <p>Loading stores...</p>;
  if (error) return <p>Error loading stores: {error.message}</p>;

  function toggleTag(tag) {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  }

  function clearFilters() {
    setSelectedTags([]);
  }

  return (
    <main className="homePage">
      {/* Text and Search Bar over header image */}
      <div className="search-bar-div">
        <div className="search-bar-text">
          Discover Unique. Support Local. <br></br> Shop Indie Index
        </div>
        <div className="search-bar-wrapper">
          <input
            className="search-bar"
            type="text"
            placeholder="Search stores, tags, descriptions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="search-icon">
            <FiSearch size={18} />
          </span>
        </div>
      </div>

      <header
        className="header-image"
        style={{
          backgroundImage: `url(${banner})`,
        }}
      ></header>

      {/* Fixed vertical Filter by tag button on left */}
      
      <div className="sort-filter-bar">
        <button
        className="open-tag-sidebar-btn "
        onClick={() => setSidebarOpen(true)}
        aria-label="Open tag filter sidebar"
      >
        <span className="">Filter by tag</span>
      </button>
        
        <div className="sort-options">
          <label htmlFor="sortBy" className="sort-label">Sort by:</label>
          <select
            id="sortBy"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="favorites-first">Favorites First</option>
            <option value="name-asc">A-Z</option>
            <option value="name-desc">Z-A</option>
            <option value="oldest">Oldest First</option>
            <option value="newest">Newest First</option>
          </select>
        </div>
      </div>

      {/* Tag Filter Sidebar */}
      <div className={`tag-sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="tag-sidebar-header">
          <span className="tag-sidebar-title">Filter by tag</span>
          <button
            className="close-tag-sidebar-btn"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close tag filter sidebar"
          >
            ×
          </button>
        </div>
        <div className="tag-sidebar-content">
          <div className="tag-filter-list">
            {allTags.length > 0 ? (
              allTags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={`tag-filter-btn${selectedTags.includes(tag) ? ' selected' : ''}`}
                >
                  {tag}
                </button>
              ))
            ) : (
              <span className="no-tags">No tags</span>
            )}
          </div>
          {selectedTags.length > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="clear-tags-btn"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      {/* Overlay for sidebar */}
      {sidebarOpen && <div className="tag-sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}

      <div className="homeContentWrapper">
        {/* Stores section */}
        <section className="storesSection">
          <div className="stores-container ">
            {filteredAndSortedStores.length > 0 ? (
              filteredAndSortedStores.map((store) => (
                <Store key={store._id} store={store} />
              ))
            ) : (
              <div className="no-stores-message">
                <p>No stores found matching all selected tags.</p>
                {selectedTags.length > 0 && (
                  <p>Try selecting fewer tags or clearing your filters.</p>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

export default Home;
